type CacheEntry = {
  promise: Promise<string>;
  objectUrl?: string;
};

const lottieCache = new Map<string, CacheEntry>();
const preloadedUrls = new Set<string>();
let lcpPromise: Promise<void> | null = null;
let preloadPromise: Promise<void> | null = null;
let dotLottieRuntimePromise: Promise<any> | null = null;
let revokeRegistered = false;
let preloadStartResolve: (() => void) | null = null;
const preloadStartPromise = new Promise<void>((resolve) => {
  preloadStartResolve = resolve;
});

const waitForLCP = () => {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  if (!('PerformanceObserver' in window)) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    let settled = false;
    let observer: PerformanceObserver | null = null;

    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      observer?.disconnect();
      resolve();
    };

    try {
      observer = new PerformanceObserver((list) => {
        if (list.getEntries().length > 0) {
          finish();
        }
      });
      observer.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch {
      finish();
      return;
    }

    window.addEventListener(
      'visibilitychange',
      () => {
        if (document.visibilityState === 'hidden') {
          finish();
        }
      },
      { once: true }
    );

    setTimeout(finish, 4000);
  });
};

const registerRevokeHandler = () => {
  if (revokeRegistered || typeof window === 'undefined') {
    return;
  }
  revokeRegistered = true;
  window.addEventListener('unload', () => {
    lottieCache.forEach((entry) => {
      if (entry.objectUrl) {
        URL.revokeObjectURL(entry.objectUrl);
      }
    });
  });
};

export const getLottieObjectUrl = (url: string) => {
  if (!url) {
    return Promise.resolve('');
  }

  const cached = lottieCache.get(url);
  if (cached?.objectUrl) {
    return Promise.resolve(cached.objectUrl);
  }
  if (cached?.promise) {
    return cached.promise;
  }

  registerRevokeHandler();

  let promise: Promise<string>;
  promise = fetch(url, {
    mode: 'cors',
    credentials: 'omit',
    cache: 'force-cache',
  })
    .then((res) => res.blob())
    .then((blob) => {
      const objectUrl = URL.createObjectURL(blob);
      lottieCache.set(url, { promise, objectUrl });
      return objectUrl;
    })
    .catch((err) => {
      lottieCache.delete(url);
      throw err;
    });

  lottieCache.set(url, { promise });
  return promise;
};

export const preloadLottieAfterLCP = (urls: string[]) => {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  if (!lcpPromise) {
    lcpPromise = waitForLCP();
  }

  if (!preloadPromise) {
    preloadStartResolve?.();
    preloadPromise = lcpPromise.then(() => {
      const tasks = urls.map((url) => {
        if (!url || preloadedUrls.has(url)) {
          return Promise.resolve();
        }
        preloadedUrls.add(url);
        return getLottieObjectUrl(url).catch(() => null);
      });
      return Promise.all(tasks).then(() => undefined);
    });
  }

  return preloadPromise;
};

export const waitForLottiePreload = () => {
  return preloadStartPromise.then(() => preloadPromise ?? Promise.resolve());
};

export const preloadDotLottieRuntime = () => {
  if (typeof window === 'undefined') {
    return Promise.resolve(null);
  }
  if (!dotLottieRuntimePromise) {
    dotLottieRuntimePromise = import('@lottiefiles/dotlottie-react').catch(() => null);
  }
  return dotLottieRuntimePromise;
};
